import React from 'react';
import { Box, IconButton } from '@mui/material';
import { PlayArrow, Pause } from '@mui/icons-material';
import { SONOS_BLUE } from './sonosUiStyles';
import { POWER_BUTTON_SIZE } from '../../styles/deviceStyles';
import { POWER_ICON_FONT_SIZE, POWER_RING_IDLE_PX } from '../shared/CardStatusPowerChrome';

interface CircularProgressPlayButtonProps {
  playing: boolean;
  progress: number;
  disabled?: boolean;
  size?: number;
  onClick: () => void;
}

/**
 * Play/pausa con anillo de progreso de track.
 *
 * Anillo = un solo conic-gradient + mask (banda exterior). Blanco y azul
 * comparten el mismo centro/radio — sin dos strokes SVG que desfasen por AA.
 * Grosor = POWER_RING_IDLE_PX (igual Shelly OFF). Ø = size (igual PowerButton).
 * Reproduciendo: fondo blanco + ícono azul (como power ON).
 */
const CircularProgressPlayButton: React.FC<CircularProgressPlayButtonProps> = ({
  playing,
  progress,
  disabled,
  size = POWER_BUTTON_SIZE,
  onClick,
}) => {
  const ringStroke =
    size === POWER_BUTTON_SIZE
      ? POWER_RING_IDLE_PX
      : Math.max(1.5, Math.round(POWER_RING_IDLE_PX * (size / POWER_BUTTON_SIZE) * 2) / 2);
  const pct = Math.max(0, Math.min(1, progress));
  const deg = pct * 360;
  const iconSize = size === POWER_BUTTON_SIZE ? POWER_ICON_FONT_SIZE : size * 0.45;

  // conic-gradient: 0deg = las 12 (arriba). No usar -90deg (eso es las 9; era el offset de SVG).
  const ringGradient = `conic-gradient(from 0deg, ${SONOS_BLUE} 0deg ${deg}deg, #ffffff ${deg}deg 360deg)`;
  // Solo la corona exterior de grosor ringStroke (resto transparente → se ve el fondo del botón).
  const ringMask = `radial-gradient(farthest-side, transparent calc(100% - ${ringStroke}px), #000 calc(100% - ${ringStroke}px))`;

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        maxWidth: size,
        maxHeight: size,
        flexShrink: 0,
        boxSizing: 'border-box',
        lineHeight: 0,
        borderRadius: '50%',
        backgroundColor: playing ? '#FFFFFF' : '#383838',
        transition: 'background-color 0.3s ease-in-out',
      }}
    >
      {/* Anillo concéntrico único (progreso azul + resto blanco). */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          background: ringGradient,
          WebkitMaskImage: ringMask,
          maskImage: ringMask,
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskSize: '100% 100%',
          maskSize: '100% 100%',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      <IconButton
        disabled={disabled}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        sx={{
          position: 'absolute',
          inset: 0,
          width: size,
          height: size,
          minWidth: size,
          minHeight: size,
          maxWidth: size,
          maxHeight: size,
          borderRadius: '50%',
          backgroundColor: 'transparent',
          color: playing ? SONOS_BLUE : '#fff',
          p: 0,
          m: 0,
          zIndex: 2,
          boxShadow: 'none',
          boxSizing: 'border-box',
          transition: 'color 0.3s ease-in-out',
          '&:hover': { backgroundColor: 'transparent' },
          '&.Mui-disabled': { color: '#666', opacity: 0.5 },
        }}
      >
        {playing
          ? <Pause sx={{ fontSize: iconSize, display: 'block' }} />
          : <PlayArrow sx={{ fontSize: iconSize, display: 'block' }} />}
      </IconButton>
    </Box>
  );
};

export default CircularProgressPlayButton;
